/**
 * Registration invite link for recipients who do not yet have a
 * Stabletrust Pay account. This is a normal public signup link — not an
 * invite code, invite-only gate, or access-control mechanism.
 */

const PRODUCTION_APP_ORIGIN = "https://app.stabletrust.io";

/**
 * Canonical URL to share with someone who needs to register.
 * Prefer REACT_APP_APP_ORIGIN when set (staging / custom deploys).
 * Localhost shares the production URL so recipients never get a dead link.
 */
export function getRegistrationInviteUrl() {
  const configured = (process.env.REACT_APP_APP_ORIGIN || "").trim();
  if (configured) {
    return `${configured.replace(/\/+$/, "")}/`;
  }

  if (typeof window !== "undefined") {
    const { hostname, origin } = window.location;
    if (hostname === "localhost" || hostname === "127.0.0.1") {
      return `${PRODUCTION_APP_ORIGIN}/`;
    }
    return `${origin.replace(/\/+$/, "")}/`;
  }

  return `${PRODUCTION_APP_ORIGIN}/`;
}

/** Short message suitable for SMS, WhatsApp, email, etc. */
export function getRegistrationInviteShareText(url = getRegistrationInviteUrl()) {
  return (
    `You're invited to Stabletrust Pay. Open this link and create your ` +
    `confidential account to receive payments: ${url}`
  );
}

export async function copyTextToClipboard(text) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }

  // Fallback for older browsers / insecure contexts
  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.setAttribute("readonly", "");
  textarea.style.position = "fixed";
  textarea.style.left = "-9999px";
  document.body.appendChild(textarea);
  textarea.select();
  document.execCommand("copy");
  document.body.removeChild(textarea);
}

/** True when the OS share sheet is available (typical on phones). */
export function canUseNativeShare() {
  return typeof navigator !== "undefined" && typeof navigator.share === "function";
}

/**
 * Build a Web Share API payload that works across iOS/Android.
 * Prefer text-only (message already includes the URL) — passing both
 * `text` and `url` often duplicates the link or fails canShare() checks.
 */
function buildNativeShareData() {
  const url = getRegistrationInviteUrl();
  const text = getRegistrationInviteShareText(url);
  return { title: "Join Stabletrust Pay", text };
}

/**
 * Open the system share sheet (Messages, WhatsApp, Mail, etc.).
 * Falls back to copying the message when native share is unavailable.
 * Returns "shared" | "copied" | "cancelled".
 */
export async function shareRegistrationInvite() {
  const text = getRegistrationInviteShareText();
  const data = buildNativeShareData();

  if (canUseNativeShare()) {
    try {
      if (
        typeof navigator.canShare === "function" &&
        !navigator.canShare(data)
      ) {
        // Rare: payload rejected — try URL-only before giving up.
        const urlOnly = { url: getRegistrationInviteUrl() };
        if (navigator.canShare(urlOnly)) {
          await navigator.share(urlOnly);
          return "shared";
        }
      } else {
        await navigator.share(data);
        return "shared";
      }
    } catch (err) {
      // User dismissed the sheet — not an error.
      if (err && err.name === "AbortError") {
        return "cancelled";
      }
      // Fall through to clipboard on unexpected share failures.
    }
  }

  await copyTextToClipboard(text);
  return "copied";
}
